import { z } from 'zod';
/** Alarm types the edge may publish to MQTT …/telemetry/alarms */
export const LocationEdgeAlarmTypeSchema = z.enum([
    'SOS',
    'LOW_BATTERY',
    'OFFLINE',
    'ONLINE',
    'REGION_ENTER',
    'REGION_EXIT',
]);
/** Default edge publish allow-list: battery + tag online/offline connectivity. */
export const DEFAULT_PUBLISHED_ALARM_TYPES = [
    'LOW_BATTERY',
    'ONLINE',
    'OFFLINE',
];
/** UDP ingest bind target for Woxu LS-1000 datagrams on the edge orchestrator. */
export const LocationUdpListenEndpointSchema = z.object({
    host: z.string().min(1),
    port: z.number().int().min(1).max(65535),
});
/** Production default: co-located LS-1000 forwards to loopback only. */
export const DEFAULT_UDP_LISTEN_ENDPOINTS = [
    { host: '127.0.0.1', port: 5000 },
];
/** Woxu UDP position emit: min 3-D movement (cm) before qualifying for position batch. */
export const DEFAULT_WOXU_MOVEMENT_THRESHOLD_CM = 15;
/** Woxu UDP position emit: max interval (ms) to batch sub-threshold movement into one MQTT publish. */
export const DEFAULT_WOXU_POSITION_MAX_SILENCE_MS = 2000;
/** Woxu status MQTT: min interval (ms) between routine status publishes per device. */
export const DEFAULT_WOXU_STATUS_MAX_SILENCE_MS = 30000;
/** Woxu occupancy MQTT: min interval (ms) between layer occupancy publishes. */
export const DEFAULT_WOXU_OCCUPANCY_MIN_INTERVAL_MS = 30000;
/** Valkey TTL for live tag current-state / motion / Kalman keys when site omits currentStateTtlSec. */
export const DEFAULT_CURRENT_STATE_TTL_SEC = 30 * 60;
export function formatUdpListenEndpoint(endpoint) {
    return `${endpoint.host}:${endpoint.port}`;
}
export function normalizeUdpListenEndpoints(endpoints) {
    const seen = new Set();
    const normalized = [];
    for (const endpoint of endpoints) {
        const key = formatUdpListenEndpoint(endpoint);
        if (seen.has(key))
            continue;
        seen.add(key);
        normalized.push(endpoint);
    }
    return normalized;
}
export const LocationTelemetrySettingsSchema = z.object({
    /** Edge MQTT alarm publish allow-list. */
    publishedAlarmTypes: z
        .array(LocationEdgeAlarmTypeSchema)
        .default([...DEFAULT_PUBLISHED_ALARM_TYPES]),
    /** Edge orchestrator UDP ingest bind targets (written to orchestrator-settings.json on gateway push). */
    udpListenEndpoints: z
        .array(LocationUdpListenEndpointSchema)
        .min(1)
        .default([...DEFAULT_UDP_LISTEN_ENDPOINTS])
        .transform(normalizeUdpListenEndpoints),
    /** Cloud: site master switch for zone alarm pipeline. Default: false (opt-in). */
    cloudZoneAlarmsEnabled: z.boolean().default(false),
    /**
     * Cloud: provider/testing override — evaluate zone alarms for every tag at the site
     * without per-tag geofencingEnabled. Tenant UI does not expose this.
     */
    cloudZoneAlarmsAllTagsEnabled: z.boolean().default(false),
    /** Cloud: min seconds between zone re-eval debounce per tag (boundary flicker). */
    zoneAlarmDebounceSec: z.number().min(0).max(60).default(2),
    /** Cloud: consecutive pos messages in new zone before emitting ENTER. */
    zoneEnterConfirmCount: z.number().int().min(1).max(10).default(2),
    /** Kalman measurement noise variance R (m²). UWB default σ≈0.14m → 0.02. */
    kalmanR: z.number().positive().max(100).default(0.15),
    /** Chi-squared innovation gate threshold (2 DOF). 6.0 ≈ 95% confidence. */
    innovationGateThreshold: z.number().positive().max(20).default(6),
    /** STATIONARY → MOVING distance from anchor (m). */
    moveThresholdM: z.number().positive().max(50).default(0.25),
    /** Movement below this counts as still (m). */
    stillThresholdM: z.number().positive().max(10).default(0.15),
    /** Seconds without movement before MOVING → STATIONARY. */
    dwellWindowSec: z.number().int().min(5).max(600).default(120),
    /** Option C: min smoothed distance between movement history writes (m). */
    distanceWriteThresholdM: z.number().positive().max(100).default(0.25),
    /** Valkey TTL for current-state keys (seconds). Default 30 min when omitted. */
    currentStateTtlSec: z.number().int().min(60).max(86400).optional(),
    /** Edge Woxu: min movement (cm) before MQTT position publish. */
    movementThresholdCm: z.number().positive().max(500).default(DEFAULT_WOXU_MOVEMENT_THRESHOLD_CM),
    /** Edge Woxu: max interval (ms) between MQTT publishes while movement stays below threshold (batches latest fix). */
    positionMaxSilenceMs: z.number().int().min(500).max(600000).default(DEFAULT_WOXU_POSITION_MAX_SILENCE_MS),
    /** Edge Woxu: min interval (ms) between routine MQTT status publishes per device. */
    statusMaxSilenceMs: z.number().int().min(1000).max(600000).default(DEFAULT_WOXU_STATUS_MAX_SILENCE_MS),
    /** Edge Woxu: min interval (ms) between MQTT occupancy publishes per layer. */
    occupancyMinIntervalMs: z.number().int().min(1000).max(600000).default(DEFAULT_WOXU_OCCUPANCY_MIN_INTERVAL_MS),
});
/** Partial settings for merges (tenant/site/gateway overrides). */
export const LocationTelemetrySettingsPartialSchema = LocationTelemetrySettingsSchema.partial();
export const LocationEdgeTelemetrySettingsSchema = z.object({
    publishedAlarmTypes: LocationTelemetrySettingsSchema.shape.publishedAlarmTypes,
    udpListenEndpoints: LocationTelemetrySettingsSchema.shape.udpListenEndpoints,
    movementThresholdCm: LocationTelemetrySettingsSchema.shape.movementThresholdCm,
    positionMaxSilenceMs: LocationTelemetrySettingsSchema.shape.positionMaxSilenceMs,
    statusMaxSilenceMs: LocationTelemetrySettingsSchema.shape.statusMaxSilenceMs,
    occupancyMinIntervalMs: LocationTelemetrySettingsSchema.shape.occupancyMinIntervalMs,
});
export const LocationEdgeTelemetrySettingsPartialSchema = LocationEdgeTelemetrySettingsSchema.partial();
export const LocationCloudTelemetrySettingsSchema = LocationTelemetrySettingsSchema.pick({
    cloudZoneAlarmsEnabled: true,
    cloudZoneAlarmsAllTagsEnabled: true,
    zoneAlarmDebounceSec: true,
    zoneEnterConfirmCount: true,
    kalmanR: true,
    innovationGateThreshold: true,
    moveThresholdM: true,
    stillThresholdM: true,
    dwellWindowSec: true,
    distanceWriteThresholdM: true,
    currentStateTtlSec: true,
});
export const LocationCloudTelemetrySettingsPartialSchema = LocationCloudTelemetrySettingsSchema.partial();
/** Edge manifest component — only fields consumed on gateway. */
export const LocationOrchestratorSettingsSchema = z.object({
    publishedAlarmTypes: LocationTelemetrySettingsSchema.shape.publishedAlarmTypes,
    udpListenEndpoints: LocationTelemetrySettingsSchema.shape.udpListenEndpoints,
    movementThresholdCm: LocationTelemetrySettingsSchema.shape.movementThresholdCm,
    positionMaxSilenceMs: LocationTelemetrySettingsSchema.shape.positionMaxSilenceMs,
    statusMaxSilenceMs: LocationTelemetrySettingsSchema.shape.statusMaxSilenceMs,
    occupancyMinIntervalMs: LocationTelemetrySettingsSchema.shape.occupancyMinIntervalMs,
});
export const PLATFORM_LOCATION_TELEMETRY_DEFAULTS = LocationTelemetrySettingsSchema.parse({});
const WOXU_ALARM_ALIASES = {
    RGNENTE: 'REGION_ENTER',
    RGN_IN: 'REGION_ENTER',
    GATE_IN: 'REGION_ENTER',
    RGNEXIT: 'REGION_EXIT',
    RGN_OUT2: 'REGION_EXIT',
    GATE_OUT: 'REGION_EXIT',
};
/**
 * Normalise raw Woxu / vendor alarm codes to canonical edge alarm types.
 */
export function normaliseEdgeAlarmType(raw) {
    const trimmed = raw.trim();
    if (!trimmed)
        return trimmed;
    if (trimmed.startsWith('SOS'))
        return 'SOS';
    if (trimmed === 'RGN__IN')
        return 'REGION_ENTER';
    if (trimmed === 'RGN_OUT')
        return 'REGION_EXIT';
    if (trimmed === 'OFFLINE')
        return 'OFFLINE';
    if (trimmed === 'ONLINE')
        return 'ONLINE';
    if (trimmed.startsWith('T_LOW') || trimmed.startsWith('LOW_BAT'))
        return 'LOW_BATTERY';
    const alias = WOXU_ALARM_ALIASES[trimmed];
    if (alias)
        return alias;
    return trimmed;
}
/** Effective Redis TTL for live tag state (current position, motion machine, Kalman). */
export function resolveCurrentStateTtlSec(settings) {
    return settings.currentStateTtlSec ?? DEFAULT_CURRENT_STATE_TTL_SEC;
}
export function shouldPublishEdgeAlarm(alarmTypeRaw, settings) {
    const normalised = normaliseEdgeAlarmType(alarmTypeRaw);
    const allowed = LocationEdgeAlarmTypeSchema.safeParse(normalised);
    if (!allowed.success)
        return false;
    return settings.publishedAlarmTypes.includes(allowed.data);
}
//# sourceMappingURL=location-telemetry-settings.schema.js.map